import React, { useState, useMemo, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import {
  ChevronLeft,
  ChevronRight,
  Printer,
  Calendar,
  Edit2,
  X,
  Plus,
  Trash2,
  Sparkles,
} from 'lucide-react';
import { fmt, fmtDate, eventSpansDate } from '../lib/utils';
import { FunctionSheetBody } from '../components/FunctionSheetRenderer';
import { getOpsVisibilityNotes } from '../lib/eventCompleteness';
import TimeSelect from '../components/TimeSelect';
import DailySheetV2, { WeeklySheetV2 } from '../components/DailySheetV2';
import OccupancyPanel from '../components/OccupancyPanel';

// Local YYYY-MM-DD (not UTC) so date math stays consistent with event.date strings
// and the <input type="date"> value — avoids off-by-one-day bugs near midnight.
function toLocalYMD(d) {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fromYMD(str) {
  if (!str) return new Date();
  const [y, m, d] = String(str).split('-').map(Number);
  // Local midnight — sidesteps UTC parsing of "YYYY-MM-DD" in Date().
  return new Date(y, (m || 1) - 1, d || 1);
}

export default function FunctionSheets({ onNavigate, onSelectEvent, selectedEvent: propSelectedEvent, navigateAction, clearNavigateAction }) {
  const {
    events,
    venues,
    payments,
    spaBookings = [],
    addSpaBooking,
    updateSpaBooking,
    deleteSpaBooking,
    tableBookings = [],
    restaurantPackages = [],
    dbLoading,
    dbError,
  } = useAppContext();
  const [view, setView] = useState(propSelectedEvent ? 'event' : 'daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEventId, setSelectedEventId] = useState(propSelectedEvent?.id || '');

  // Get week start (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  // Get days of week for weekly view
  const getDaysOfWeek = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Get events for selected date (daily view)
  const getDailyEvents = () => {
    const dateStr = toLocalYMD(selectedDate);
    return events
      .filter((evt) => eventSpansDate(evt, dateStr))
      .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  };

  // Get events for a week (weekly view).
  // Compare by yyyy-MM-dd strings (local) to avoid UTC/DST edge cases.
  const getWeeklyEvents = () => {
    const weekStart = getWeekStart(selectedDate);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const startKey = toLocalYMD(weekStart);
    const endKey = toLocalYMD(weekEnd);

    return events.filter((evt) => {
      if (!evt.date) return false;
      // Overlap test so multi-day events that start before / end after the week still show.
      return evt.date <= endKey && (evt.endDate || evt.date) >= startKey;
    });
  };

  // Get non-complete events for dropdown
  const availableEvents = useMemo(
    () =>
      events
        .filter((evt) => evt.stage !== 'complete')
        .sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [events]
  );

  // Get selected event
  const selectedEvent = useMemo(
    () => events.find((evt) => evt.id === selectedEventId),
    [selectedEventId, events]
  );

  // Navigation handlers
  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handlePrevWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedDate(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedDate(newDate);
  };

  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [printSingleEvent, setPrintSingleEvent] = useState(null);

  const handlePrint = () => {
    setShowPrintPreview(true);
  };

  // Handle incoming navigation requests from other pages
  // navigateAction shapes supported:
  //   { action: 'print-daily', date: 'YYYY-MM-DD' }  → switch to daily view for that date and open print preview
  //   { action: 'open-daily',  date: 'YYYY-MM-DD' }  → switch to daily view for that date (no print)
  useEffect(() => {
    if (!navigateAction || typeof navigateAction !== 'object') return;
    const { action, date } = navigateAction;
    if (date) {
      // Parse YYYY-MM-DD as local date (avoid UTC shift)
      const [y, m, d] = String(date).split('-').map(Number);
      if (y && m && d) {
        setSelectedDate(new Date(y, m - 1, d));
      }
    }
    if (action === 'print-daily' || action === 'open-daily') {
      setView('daily');
      setSelectedEventId('');
    }
    if (action === 'print-daily') {
      // Small delay so state updates commit before opening the preview
      setTimeout(() => setShowPrintPreview(true), 0);
    }
    if (action === 'print-weekly') {
      setView('weekly');
      setSelectedEventId('');
      setTimeout(() => setShowPrintPreview(true), 0);
    }
    if (typeof clearNavigateAction === 'function') clearNavigateAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateAction]);

  const handlePrintEvent = (evt) => {
    setPrintSingleEvent(evt);
  };

  const handleEditEvent = (eventId) => {
    const event = events.find((evt) => evt.id === eventId);
    if (event && onSelectEvent && onNavigate) {
      onSelectEvent(event);
      onNavigate('events');
    }
  };

  // ============ WEDDING DETAILS HELPER ============
  const isWedding = (evt) => evt.type?.toLowerCase() === 'wedding';

  const WeddingDetailsSection = ({ event, variant = 'card' }) => {
    const wd = event.weddingDetails || {};
    const borderClass = variant === 'card' ? 'pt-2 border-t border-gray-100' : 'mb-4 pb-4 border-b border-gray-200';
    const labelClass = 'text-xs font-semibold text-gray-500 uppercase mb-1';

    return (
      <>
        {/* Bride & Groom */}
        {(event.brideName || event.groomName) && (
          <div className={borderClass}>
            <p className={labelClass}>Bride & Groom</p>
            <p className="text-sm text-gray-900 font-semibold">
              {[event.brideName, event.groomName].filter(Boolean).join(' & ')}
            </p>
          </div>
        )}

        {/* Ceremony */}
        {(wd.ceremonyType || wd.ceremonyTime || wd.ceremonyLocation) && (
          <div className={borderClass}>
            <p className={labelClass}>Ceremony</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.ceremonyType && <p>Type: <span className="capitalize">{wd.ceremonyType}</span></p>}
              {wd.ceremonyTime && <p>Time: {wd.ceremonyTime}</p>}
              {wd.ceremonyLocation && <p>Location: {wd.ceremonyLocation}</p>}
              {wd.officiant && <p>Officiant: {wd.officiant}</p>}
            </div>
          </div>
        )}

        {/* Arrival Reception */}
        {(wd.arrivalDrinks || wd.arrivalCanapes || wd.arrivalMusic) && (
          <div className={borderClass}>
            <p className={labelClass}>Arrival Reception</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.arrivalDrinks && <p>Drinks: {wd.arrivalDrinks}</p>}
              {wd.arrivalCanapes && <p>Canapes: {wd.arrivalCanapes}</p>}
              {wd.arrivalMusic && <p>Music: {wd.arrivalMusic}</p>}
            </div>
          </div>
        )}

        {/* Top Table */}
        {(wd.topTableSize || wd.topTableLayout || wd.topTableNames) && (
          <div className={borderClass}>
            <p className={labelClass}>Top Table</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.topTableSize && <p>Size: {wd.topTableSize} guests</p>}
              {wd.topTableLayout && <p>Layout: {wd.topTableLayout}</p>}
              {wd.topTableNames && <p>Names: {wd.topTableNames}</p>}
            </div>
          </div>
        )}

        {/* Meal / Menu */}
        {(wd.mealTime || wd.starterOption || wd.mainOption || wd.dessertOption || wd.menuChoice) && (
          <div className={borderClass}>
            <p className={labelClass}>Wedding Menu</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.mealTime && <p>Meal Time: {wd.mealTime}</p>}
              {wd.menuChoice && <p>Menu: {wd.menuChoice}</p>}
              {wd.starterOption && <p>Starter: {wd.starterOption}</p>}
              {wd.mainOption && <p>Main: {wd.mainOption}</p>}
              {wd.dessertOption && <p>Dessert: {wd.dessertOption}</p>}
              {wd.childrensMeal && <p>Children's Meal: {wd.childrensMeal}</p>}
              {wd.dietaryNotes && <p>Dietary Notes: {wd.dietaryNotes}</p>}
            </div>
          </div>
        )}

        {/* Drinks Package */}
        {(wd.wineSelection || wd.toastDrink || wd.barTab || wd.drinksPackage) && (
          <div className={borderClass}>
            <p className={labelClass}>Drinks</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.drinksPackage && <p>Package: {wd.drinksPackage}</p>}
              {wd.wineSelection && <p>Wine: {wd.wineSelection}</p>}
              {wd.toastDrink && <p>Toast: {wd.toastDrink}</p>}
              {wd.barTab && <p>Bar Tab: {wd.barTab}</p>}
            </div>
          </div>
        )}

        {/* Decor & Cake */}
        {(wd.tableDecor || wd.centrepieces || wd.cakeDetails || wd.cakeSupplier) && (
          <div className={borderClass}>
            <p className={labelClass}>Decor & Cake</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.tableDecor && <p>Table Decor: {wd.tableDecor}</p>}
              {wd.centrepieces && <p>Centrepieces: {wd.centrepieces}</p>}
              {wd.cakeDetails && <p>Cake: {wd.cakeDetails}</p>}
              {wd.cakeSupplier && <p>Cake Supplier: {wd.cakeSupplier}</p>}
            </div>
          </div>
        )}

        {/* Entertainment */}
        {(wd.band || wd.dj || wd.firstDance || wd.eveningFood) && (
          <div className={borderClass}>
            <p className={labelClass}>Entertainment</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.band && <p>Band: {wd.band}</p>}
              {wd.dj && <p>DJ: {wd.dj}</p>}
              {wd.firstDance && <p>First Dance: {wd.firstDance}</p>}
              {wd.eveningFood && <p>Evening Food: {wd.eveningFood}</p>}
            </div>
          </div>
        )}

        {/* Suppliers */}
        {(wd.photographer || wd.videographer || wd.florist || wd.celebrant) && (
          <div className={borderClass}>
            <p className={labelClass}>Suppliers</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.photographer && <p>Photographer: {wd.photographer}</p>}
              {wd.videographer && <p>Videographer: {wd.videographer}</p>}
              {wd.florist && <p>Florist: {wd.florist}</p>}
              {wd.celebrant && <p>Celebrant: {wd.celebrant}</p>}
            </div>
          </div>
        )}

        {/* Accommodation */}
        {(wd.brideRoom || wd.groomRoom || wd.guestRooms || wd.accommodationNotes) && (
          <div className={borderClass}>
            <p className={labelClass}>Accommodation</p>
            <div className="text-sm text-gray-700 space-y-1">
              {wd.brideRoom && <p>Bride's Room: {wd.brideRoom}</p>}
              {wd.groomRoom && <p>Groom's Room: {wd.groomRoom}</p>}
              {wd.guestRooms && <p>Guest Rooms: {wd.guestRooms}</p>}
              {wd.accommodationNotes && <p>Notes: {wd.accommodationNotes}</p>}
            </div>
          </div>
        )}
      </>
    );
  };

  // ============ EVENT CARD COMPONENT ============
  // Uses the shared FunctionSheetBody (Option C layout) for the body, so
  // every function-sheet surface in the app reads identically. The card
  // just adds a gradient wrapper with the Print / Edit action buttons
  // floated to the right — weddings get a pink tint, other events blue.
  const EventCard = ({ event, showEditButton = true, showOccupancy = true }) => {
    const wedding = isWedding(event);

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
        {/* Action bar — hidden when printing so the sheet is clean. */}
        <div
          className={`no-print border-b border-gray-200 px-4 md:px-6 py-2 flex items-center justify-end gap-1 ${
            wedding
              ? 'bg-gradient-to-r from-pink-50 to-rose-50'
              : 'bg-gradient-to-r from-blue-50 to-indigo-50'
          }`}
        >
          <button
            onClick={() => handlePrintEvent(event)}
            className="p-2 rounded-lg hover:bg-white transition-colors"
            title="Print function sheet"
          >
            <Printer size={18} className="text-gray-600" />
          </button>
          {showEditButton && (
            <button
              onClick={() => handleEditEvent(event.id)}
              className="p-2 rounded-lg hover:bg-white transition-colors"
              title="Edit event"
            >
              <Edit2 size={18} className="text-blue-600" />
            </button>
          )}
        </div>

        {/* Why this event looks different on the daily sheet / kitchen (screen only). */}
        {getOpsVisibilityNotes(event).map((n, i) => (
          <div key={i} className={`no-print px-4 md:px-6 py-1.5 text-sm border-b ${n.tone === 'warn' ? 'bg-amber-50 text-amber-900 border-amber-200 font-semibold' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
            {n.tone === 'warn' ? '⚠ ' : ''}{n.text}
          </div>
        ))}

        {/* Sheet body */}
        <div className="px-4 md:px-6 py-4">
          <FunctionSheetBody event={event} venues={venues} showOccupancy={showOccupancy} />
          {wedding && (
            <div className="mt-4">
              <WeddingDetailsSection event={event} variant="card" />
            </div>
          )}
        </div>
      </div>
    );
  };

  // ============ SPA BOOKINGS PANEL (screen-only, manual reception entry) ============
  // Lets the receptionist add/edit/delete spa bookings for the selected date.
  // Bookings flow into the Daily Function Sheet print so service staff see them
  // alongside events.
  const SpaBookingsPanel = () => {
    const dateStr = toLocalYMD(selectedDate);
    const daySpa = (spaBookings || [])
      .filter((b) => b.date === dateStr)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

    const emptyForm = { time: '', guestName: '', details: '', covers: '', deposit: '', comments: '' };
    const [draft, setDraft] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const startEdit = (b) => {
      setEditingId(b.id);
      setDraft({
        time: b.time || '',
        guestName: b.guestName || '',
        details: b.details || '',
        covers: b.covers ?? '',
        deposit: b.deposit ?? '',
        comments: b.comments || '',
      });
      setShowForm(true);
    };

    const cancel = () => {
      setEditingId(null);
      setDraft(emptyForm);
      setShowForm(false);
    };

    const save = async () => {
      const payload = {
        date: dateStr,
        time: draft.time.trim(),
        guestName: draft.guestName.trim(),
        details: draft.details.trim(),
        covers: draft.covers === '' ? null : Number(draft.covers),
        deposit: draft.deposit === '' ? null : Number(draft.deposit),
        comments: draft.comments.trim(),
      };
      if (!payload.guestName && !payload.time && !payload.details) return;
      try {
        if (editingId) {
          await updateSpaBooking(editingId, payload);
        } else {
          await addSpaBooking(payload);
        }
        cancel();
      } catch (err) {
        console.error('Spa booking save failed:', err);
        alert('Could not save spa booking — please try again.');
      }
    };

    const remove = async (id) => {
      if (!window.confirm('Delete this spa booking?')) return;
      await deleteSpaBooking(id);
    };

    return (
      <div className="mt-8 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-3 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-purple-600" />
            <h3 className="font-bold text-gray-900">Spa Bookings</h3>
            <span className="text-sm text-gray-500">({daySpa.length})</span>
          </div>
          {!showForm && (
            <button
              onClick={() => { setEditingId(null); setDraft(emptyForm); setShowForm(true); }}
              className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
            >
              <Plus size={16} /> Add Booking
            </button>
          )}
        </div>

        {showForm && (
          <div className="p-4 md:p-6 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Time</label>
                <TimeSelect
                  value={draft.time}
                  onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                  startHour={7} endHour={21} stepMinutes={15}
                  placeholder="-- time --"
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Guest Name</label>
                <input
                  type="text"
                  value={draft.guestName}
                  onChange={(e) => setDraft({ ...draft, guestName: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Mary Smith"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Details</label>
                <input
                  type="text"
                  value={draft.details}
                  onChange={(e) => setDraft({ ...draft, details: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Massage, Facial..."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Covers</label>
                <input
                  type="number"
                  min="0"
                  value={draft.covers}
                  onChange={(e) => setDraft({ ...draft, covers: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="1"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Deposit (€)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.deposit}
                  onChange={(e) => setDraft({ ...draft, deposit: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="50"
                />
              </div>
              <div className="col-span-2 md:col-span-5">
                <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">Comments</label>
                <input
                  type="text"
                  value={draft.comments}
                  onChange={(e) => setDraft({ ...draft, comments: e.target.value })}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  placeholder="Therapist, room, allergies, notes..."
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={save}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium"
              >
                {editingId ? 'Save Changes' : 'Add Booking'}
              </button>
              <button
                onClick={cancel}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {daySpa.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm italic">
            No spa bookings for this date — add the first with the button above.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-semibold text-gray-700">Time</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Guest</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Details</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Covers</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 text-right">Deposit</th>
                  <th className="px-3 py-2 font-semibold text-gray-700">Comments</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {daySpa.map((b) => (
                  <tr key={b.id} className="border-t border-gray-200">
                    <td className="px-3 py-2 font-semibold tabular-nums">{b.time || '—'}</td>
                    <td className="px-3 py-2 font-semibold">{b.guestName || '—'}</td>
                    <td className="px-3 py-2">{b.details || ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{b.covers ?? '—'}</td>
                    <td className={`px-3 py-2 text-right tabular-nums ${b.deposit > 0 ? 'text-emerald-700 font-semibold' : 'text-gray-500'}`}>
                      {b.deposit > 0 ? fmt(b.deposit) : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{b.comments || ''}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(b)}
                          className="p-1.5 hover:bg-gray-100 rounded text-blue-600"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => remove(b.id)}
                          className="p-1.5 hover:bg-gray-100 rounded text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============ EVENT VIEW ============
  const EventFunctionSheet = () => {
    if (!selectedEvent) {
      return (
        <div className="print:p-8 p-0 bg-white">
          <div className="text-center py-12">
            <p className="text-gray-500 italic">Please select an event</p>
          </div>
        </div>
      );
    }

    return (
      <div className="print:p-8 p-0 bg-white">
        {/* Hotel Header */}
        <div className="border-b-2 border-gray-300 pb-6 mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Errigal Country House Hotel</h1>
          <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mt-2">Function Sheet</h2>
        </div>

        {/* Edit button (screen only) */}
        <div className="no-print flex justify-end mb-3">
          <button
            onClick={() => handleEditEvent(selectedEvent.id)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            <Edit2 size={16} />
            Edit Event
          </button>
        </div>

        {/* Shared Option-C body */}
        <FunctionSheetBody event={selectedEvent} venues={venues} />

        {/* Wedding-specific detail block — appended rather than inlined so
            the shared body stays agnostic. */}
        {isWedding(selectedEvent) && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <WeddingDetailsSection event={selectedEvent} variant="print" />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Top Toolbar */}
      <div className="no-print bg-white border-b border-gray-200 sticky top-0 z-40 overflow-x-auto">
        <div className="px-4 md:px-6 lg:px-8 py-4 max-w-7xl mx-auto">
          {/* Header with Print Button */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-6 mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Function Sheets</h1>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 md:px-6 py-2 rounded-lg transition-colors font-medium min-h-[44px] justify-center w-full md:w-auto"
              title={view === 'daily' ? 'Print daily function sheet (all events on this date)' : view === 'weekly' ? 'Print weekly function sheet' : 'Print function sheet for this event'}
            >
              <Printer size={20} />
              <span className="hidden md:inline">
                {view === 'daily' ? 'Print Daily Sheet' : view === 'weekly' ? 'Print Weekly Sheet' : 'Print Function Sheet'}
              </span>
            </button>
          </div>

          {/* View Toggle Tabs */}
          <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto">
            <button
              onClick={() => {
                setView('daily');
                setSelectedEventId('');
              }}
              className={`px-3 md:px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap text-sm md:text-base ${
                view === 'daily'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Daily View
            </button>
            <button
              onClick={() => {
                setView('weekly');
                setSelectedEventId('');
              }}
              className={`px-3 md:px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap text-sm md:text-base ${
                view === 'weekly'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Weekly View
            </button>
            <button
              onClick={() => setView('event')}
              className={`px-3 md:px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap text-sm md:text-base ${
                view === 'event'
                  ? 'border-blue-600 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Event View
            </button>
          </div>

          {/* View Controls */}
          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
            {/* Daily Navigation */}
            {view === 'daily' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevDay}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Previous day"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg">
                  <Calendar size={16} className="text-gray-600" />
                  <input
                    type="date"
                    value={toLocalYMD(selectedDate)}
                    onChange={(e) => setSelectedDate(fromYMD(e.target.value))}
                    className="border-0 bg-transparent text-gray-900 text-sm focus:outline-none font-medium"
                  />
                </div>
                <button
                  onClick={handleNextDay}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Next day"
                >
                  <ChevronRight size={20} className="text-gray-600" />
                </button>
              </div>
            )}

            {/* Weekly Navigation */}
            {view === 'weekly' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevWeek}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Previous week"
                >
                  <ChevronLeft size={20} className="text-gray-600" />
                </button>
                <span className="px-4 py-2 text-gray-900 font-medium">
                  {fmtDate(getWeekStart(selectedDate))} -{' '}
                  {fmtDate(new Date(getWeekStart(selectedDate).getTime() + 6 * 24 * 60 * 60 * 1000))}
                </span>
                <button
                  onClick={handleNextWeek}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Next week"
                >
                  <ChevronRight size={20} className="text-gray-600" />
                </button>
              </div>
            )}

            {/* Event Selector */}
            {view === 'event' && (
              <select
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white text-sm font-medium"
              >
                <option value="">Select an event...</option>
                {availableEvents.map((evt) => (
                  <option key={evt.id} value={evt.id}>
                    {evt.name} — {fmtDate(new Date(evt.date))}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Function Sheet Content */}
      <div className="min-h-screen bg-gray-50 print:min-h-0 print:bg-white">
        {/* Print Content - hidden on screen, shown when printing (only if preview modal is NOT open) */}
        {!showPrintPreview && !printSingleEvent && (
          <div className="hidden print:block">
            {view === 'daily' && <DailySheetV2 date={selectedDate} events={events} tableBookings={tableBookings} spaBookings={spaBookings} venues={venues} payments={payments} packages={restaurantPackages} />}
            {view === 'weekly' && <WeeklySheetV2 weekStart={getWeekStart(selectedDate)} events={events} tableBookings={tableBookings} spaBookings={spaBookings} venues={venues} payments={payments} packages={restaurantPackages} />}
            {view === 'event' && EventFunctionSheet()}
          </div>
        )}

        {/* Screen UI (Hidden in Print) */}
        <div className="no-print">
          {/* Main Content Area - Cards for Daily/Weekly, Details for Event */}
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8 max-w-7xl mx-auto">
            {/* Daily View - Card Layout */}
            {view === 'daily' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm text-gray-600">
                      {selectedDate.toLocaleDateString('en-IE', { weekday: 'long' })}
                    </p>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {fmtDate(selectedDate)}
                    </h2>
                  </div>
                  {getDailyEvents().length > 0 && (
                    <button
                      onClick={handlePrint}
                      className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                      title="Print a function sheet showing every event on this date"
                    >
                      <Printer size={16} />
                      Print Daily Function Sheet
                    </button>
                  )}
                </div>

                {/* In-house tonight — once for the day, tap to enter / edit */}
                <div className="mb-6">
                  <OccupancyPanel date={toLocalYMD(selectedDate)} />
                </div>

                {getDailyEvents().length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                    <p className="text-gray-500 text-lg">No events scheduled for this date</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:gap-6">
                    {getDailyEvents().map((evt) => (
                      <React.Fragment key={evt.id}>{EventCard({ event: evt, showEditButton: true, showOccupancy: false })}</React.Fragment>
                    ))}
                  </div>
                )}

                {/* Spa bookings — manual reception entries that print on the daily sheet */}
                <SpaBookingsPanel />
              </div>
            )}

            {/* Weekly View - Card Layout */}
            {view === 'weekly' && (
              <div>
                <div className="mb-6">
                  <p className="text-sm text-gray-600">Week of</p>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {fmtDate(getWeekStart(selectedDate))} -{' '}
                    {fmtDate(new Date(getWeekStart(selectedDate).getTime() + 6 * 24 * 60 * 60 * 1000))}
                  </h2>
                </div>

                {getWeeklyEvents().length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                    <p className="text-gray-500 text-lg">No events scheduled for this week</p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {getDaysOfWeek(getWeekStart(selectedDate)).map((date) => {
                      const dateStr = toLocalYMD(date);
                      const dayName = date.toLocaleDateString('en-IE', { weekday: 'long' });
                      const dayEvents = getWeeklyEvents().filter((evt) => eventSpansDate(evt, dateStr));

                      return (
                        <div key={dateStr}>
                          <div className="mb-4">
                            <p className="text-sm text-gray-600">{dayName}</p>
                            <h3 className="text-xl font-bold text-gray-900">{fmtDate(date)}</h3>
                          </div>
                          <div className="mb-3">
                            <OccupancyPanel date={dateStr} />
                          </div>

                          {dayEvents.length === 0 ? (
                            <p className="text-sm text-gray-400 italic">No events</p>
                          ) : (
                            <div className="grid grid-cols-1 gap-3 md:gap-4">
                              {dayEvents.map((evt) => (
                                <React.Fragment key={evt.id}>{EventCard({ event: evt, showEditButton: false, showOccupancy: false })}</React.Fragment>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Event View */}
            {view === 'event' && (
              <div>
                {!selectedEvent ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                    <p className="text-gray-500 text-lg">Please select an event from the dropdown above</p>
                  </div>
                ) : (
                  EventCard({ event: selectedEvent, showEditButton: true })
                )}
              </div>
            )}
          </div>
        </div>
      </div>

            {/* ============ Print Preview Modal ============ */}
      {showPrintPreview && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex flex-col">
          {/* Toolbar */}
          <div className="no-print bg-gray-900 text-white px-4 md:px-6 py-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6 shrink-0">
            <h2 className="text-lg font-semibold">
              Function Sheet Preview — {view === 'daily' ? 'Daily' : view === 'weekly' ? 'Weekly' : selectedEvent?.name || 'Event'}
            </h2>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button
                onClick={() => { if (!dbLoading) window.print(); }}
                disabled={dbLoading}
                title={dbLoading ? 'Still loading data — wait before printing' : undefined}
                className={`flex items-center justify-center gap-2 px-4 md:px-5 py-2 rounded-lg font-medium text-sm text-white min-h-[40px] ${
                  dbLoading ? 'bg-gray-600 cursor-not-allowed opacity-70' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                <Printer size={16} />
                <span className="hidden md:inline">{dbLoading ? 'Loading…' : 'Print'}</span>
              </button>
              <button
                onClick={() => setShowPrintPreview(false)}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          {/* Data-freshness banner — this sheet drives real kitchen/service
              prep, so a still-loading or failed fetch must never print
              silently as if it were complete/current data. */}
          {dbLoading && (
            <div className="no-print bg-amber-500 text-amber-950 px-4 md:px-6 py-2 text-sm font-semibold shrink-0">
              Data is still loading — this sheet may be incomplete. Wait a moment before printing.
            </div>
          )}
          {!dbLoading && dbError && (
            <div className="no-print bg-red-600 text-white px-4 md:px-6 py-2 text-sm font-semibold shrink-0">
              Failed to load live data ({dbError}) — this sheet may be showing outdated or sample information, not what's actually booked.
            </div>
          )}
          {/* Scrollable Sheet */}
          <div className="flex-1 overflow-y-auto overflow-x-auto bg-gray-200 p-4 md:p-6 print:p-0 print:bg-white">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-4 md:p-8 print:shadow-none print:rounded-none print:max-w-none">
              {view === 'daily' && <DailySheetV2 date={selectedDate} events={events} tableBookings={tableBookings} spaBookings={spaBookings} venues={venues} payments={payments} packages={restaurantPackages} />}
              {view === 'weekly' && <WeeklySheetV2 weekStart={getWeekStart(selectedDate)} events={events} tableBookings={tableBookings} spaBookings={spaBookings} venues={venues} payments={payments} packages={restaurantPackages} />}
              {view === 'event' && EventFunctionSheet()}
            </div>
          </div>
        </div>
      )}
      {/* ============ Print Single Event Modal ============ */}
      {printSingleEvent && (() => {
        const pEvt = printSingleEvent;
        const pWedding = isWedding(pEvt);
        return (
          <div className="fixed inset-0 bg-black/60 z-[70] flex flex-col">
            {/* Toolbar */}
            <div className="no-print bg-gray-900 text-white px-4 md:px-6 py-3 flex items-center justify-between shrink-0">
              <h2 className="text-lg font-semibold">Function Sheet — {pEvt.name}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { if (!dbLoading) window.print(); }}
                  disabled={dbLoading}
                  title={dbLoading ? 'Still loading data — wait before printing' : undefined}
                  className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium text-sm text-white ${
                    dbLoading ? 'bg-gray-600 cursor-not-allowed opacity-70' : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <Printer size={16} /> {dbLoading ? 'Loading…' : 'Print'}
                </button>
                <button
                  onClick={() => setPrintSingleEvent(null)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            {/* Scrollable Sheet */}
            <div className="flex-1 overflow-y-auto bg-gray-200 p-4 md:p-6 print:p-0 print:bg-white">
              <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-4 md:p-8 print:shadow-none print:rounded-none print:max-w-none">
                {/* Hotel Header */}
                <div className="border-b-2 border-gray-300 pb-6 mb-8">
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Errigal Country House Hotel</h1>
                  <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mt-2">Function Sheet</h2>
                </div>

                {/* Shared Option-C body */}
                <FunctionSheetBody event={pEvt} venues={venues} />

                {pWedding && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <WeddingDetailsSection event={pEvt} variant="print" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
